import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import "../../../../imageUrl";
import AuthUser from "../../../../Components/AuthUser";
import "./CurrentStock.css";
import "../../../admin_pages/drugs/Drugs.css";
import Select from "react-select";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
} from "@mui/material";
import stockFile from "../../../../front_assets/sample_file/stocks.csv";
import { toast } from "react-toastify";
import useLanguage from "../../../../Components/language/useLanguage";
import { currentStockLang } from "../../../../Components/language/pages/currentStockLang";

// Skeleton loader component matching Products List design
function StockTableSkeleton() {
  return (
    <div style={{ borderTop: "1px solid #e9ecef" }}>
      {[...Array(5)].map((_, i) => (
        <div key={i} style={{ padding: "16px", borderBottom: "1px solid #e9ecef" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "3fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr",
              gap: "16px",
              alignItems: "center"
            }}
          >
            {/* Product Column Skeleton */}
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div
                style={{
                  width: "96px",
                  height: "96px",
                  backgroundColor: "#e9ecef",
                  borderRadius: "8px",
                  flexShrink: 0,
                  animation: "pulse 1.5s ease-in-out infinite"
                }}
              ></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    height: "20px",
                    backgroundColor: "#e9ecef",
                    borderRadius: "4px",
                    width: "60%",
                    marginBottom: "8px",
                    animation: "pulse 1.5s ease-in-out infinite"
                  }}
                ></div>
                <div
                  style={{
                    height: "16px",
                    backgroundColor: "#e9ecef",
                    borderRadius: "4px",
                    width: "40%",
                    animation: "pulse 1.5s ease-in-out infinite"
                  }}
                ></div>
              </div>
            </div>
            {/* Other Columns Skeleton */}
            {[...Array(8)].map((_, j) => (
              <div
                key={j}
                style={{
                  height: "16px",
                  backgroundColor: "#e9ecef",
                  borderRadius: "4px",
                  width: "80%",
                  animation: "pulse 1.5s ease-in-out infinite"
                }}
              ></div>
            ))}
          </div>
        </div>
      ))}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}

const CurrentStock = () => {
  const { http } = AuthUser();
  const lan = useLanguage(currentStockLang);
  const [row_data_list, setRowDataList] = useState([]);
  const [stateupdate, setstateupdate] = useState();
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [spinner, setSpinner] = useState(true);
  const [brand, setBrand] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [zoomImage, setZoomImage] = useState(null);
  const searchTimeoutRef = useRef(null);
  const handleDownloadSample = () => {
    http
      .get("/current-stock-sample", { responseType: "blob" })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "current_stock_products.csv");
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(() => {
        toast.error("Failed to download sample file");
      });
  };

  // Debounce search term
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (searchTerm !== debouncedSearchTerm) {
      setIsSearching(true);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
      setIsSearching(false);
    }, 500);
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, debouncedSearchTerm]);

  useEffect(() => {
    const controller = new AbortController();
    http.get(`/current-stock`, { signal: controller.signal }).then((res) => {
      setSpinner(false);
      if (res.status === 200) {
        setRowDataList(res.data);
        setData(res.data);
      }
    });
    http.get(`/brand`, { signal: controller.signal }).then((res) => {
      setBrand(res.data.data);
    });

    return () => controller.abort();
  }, [stateupdate]);

  const handleFilter = (e) => {
    if (parseInt(e.target.value) === 1) {
      setRowDataList(data);
    } else if (parseInt(e.target.value) === 2) {
      const available_stock = data.filter((d) => d.stock && d.stock > 0);
      setRowDataList(available_stock);
    } else if (parseInt(e.target.value) === 6) {
      const reOrder = data.filter((d) => d.stock < 20 && d.stock > 1);
      setRowDataList(reOrder);
    }
  };

  const handleBrand = (item) => {
    setSelectedBrand(item);
    if (item && item.id != null) {
      const stock = data.filter((d) => Number(d.drug?.brand_id) === Number(item.id));
      setRowDataList(stock);
    } else {
      setRowDataList(data);
    }
  };

  // Memoized base image URL
  const baseImageUrl = useMemo(() => {
    const url = process.env.REACT_APP_DEV_MODE_IMAGE_URL || 
                process.env.REACT_APP_IMAGE_URL || 
                process.env.REACT_APP_PRO_MODE_IMAGE_URL || 
                global.img_url ||
                'http://127.0.0.1:8001';
    return url.replace(/\/$/, '');
  }, []);

  // Get image URL helper
  const getImageUrl = useCallback((row) => {
    let imageFileName = row.drug?.main_image || row.drug?.image || '';
    
    if (!imageFileName || 
        imageFileName === 'null' || 
        imageFileName === 'undefined' || 
        imageFileName.trim() === '') {
      return null;
    }
    
    imageFileName = imageFileName.replace(/^.*[\\\/]/, '');
    imageFileName = imageFileName.split('?')[0].split('#')[0].trim();
    
    return imageFileName && imageFileName !== ''
      ? `${baseImageUrl}/files/drugs/${imageFileName}`
      : null;
  }, [baseImageUrl]);

  // Filter and paginate data
  const filteredData = useMemo(() => {
    if (!row_data_list || row_data_list.length === 0) return [];
    if (!debouncedSearchTerm) return row_data_list;
    
    const searchLower = debouncedSearchTerm.toLowerCase();
    return row_data_list.filter((item) => {
      return (
        item.name?.toLowerCase().includes(searchLower) ||
        item.manufacturer?.toLowerCase().includes(searchLower) ||
        item.unit?.toLowerCase().includes(searchLower)
      );
    });
  }, [row_data_list, debouncedSearchTerm]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredData.slice(startIndex, startIndex + pageSize);
  }, [filteredData, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredData.length / pageSize);

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const totalPrice = row_data_list
    .reduce(
      (a, b) =>
        a +
        parseInt(b.stock || 0) *
          (parseFloat(b.drug?.drug_price ? b.drug?.drug_price : 0) +
            parseFloat(b.drug?.vat ? b.drug?.vat : 0)),
      0
    )
    .toFixed(2);
  const totalSellingPrice = row_data_list
    .reduce(
      (a, b) =>
        a + parseInt(b.stock || 0) * parseFloat(b.drug?.price ? b.drug?.price : 0),
      0
    )
    .toFixed(2);
  const total = row_data_list
    .reduce((a, b) => a + parseInt(b.stock || 0), 0)
    .toFixed(2);

  const [open, setOpen] = useState(false);
  const [csvFile, setcsvFile] = useState(null);

  const handleClose = (event, reason) => {
    if (reason !== "backdropClick") {
      setOpen(false);
      setcsvFile(null);
    }
  };

  return (
    <div className="page-content" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh", padding: "24px" }}>
      {/* Header */}
      <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h1 style={{ fontSize: "24px", fontWeight: "bold", color: "#343a40", margin: 0, fontFamily: "sans-serif" }}>
            {lan?.pageTitle || "Current Stock"}
          </h1>
          <p style={{ fontSize: "14px", color: "#6c757d", marginTop: "4px", marginBottom: 0, fontFamily: "sans-serif" }}>
            View and manage your current stock levels and inventory
          </p>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
         
          <button
  onClick={handleDownloadSample}
  style={{
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    backgroundColor: "#69b128",
    color: "white",
    padding: "8px 16px",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
  }}
>
  <i className="fa fa-download"></i>
  Sample File
</button>
 
          
          <button
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              backgroundColor: "#69b128",
              color: "white",
              padding: "8px 16px",
              borderRadius: "8px",
              border: "none",
              fontWeight: "500",
              fontFamily: "sans-serif",
              cursor: "pointer",
              transition: "background-color 0.2s"
            }}
            onClick={() => setOpen(true)}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#5a9a20";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#69b128";
            }}
          >
            <i className="fa fa-upload" aria-hidden="true"></i>
            CSV Import
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid #e9ecef",
          padding: "16px",
          marginBottom: "24px"
        }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
          style={{ display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "center" }}
        >
          <input
            type="text"
            placeholder="Search current stock..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: "1 1 200px",
              minWidth: "200px",
              padding: "8px 16px",
              border: "1px solid #e9ecef",
              borderRadius: "8px",
              fontSize: "14px",
              fontFamily: "sans-serif",
              outline: "none"
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#a855f7";
              e.target.style.boxShadow = "0 0 0 2px rgba(168, 85, 247, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e9ecef";
              e.target.style.boxShadow = "none";
            }}
          />
          <div style={{ minWidth: "200px" }}>
            <Select
              options={[{ id: null, title: "All Brands" }, ...brand]}
              value={selectedBrand ? { id: selectedBrand.id, title: selectedBrand.title } : { id: null, title: "All Brands" }}
              onChange={(option) => handleBrand(option.id ? option : null)}
              getOptionLabel={(brandName) => brandName.title}
              getOptionValue={(brandName) => brandName.id}
              placeholder="Select Brand"
              styles={{
                control: (base) => ({
                  ...base,
                  borderColor: "#e9ecef",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontFamily: "sans-serif",
                  minHeight: "38px",
                  "&:hover": {
                    borderColor: "#a855f7"
                  }
                })
              }}
            />
          </div>
          {isSearching && (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div
                className="spinner-border spinner-border-sm"
                role="status"
                style={{ width: "16px", height: "16px", borderWidth: "2px", color: "#6c757d" }}
              >
                <span className="visually-hidden">Searching...</span>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Stock List */}
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid #e9ecef",
          overflow: "hidden"
        }}
      >
        {spinner ? (
          <StockTableSkeleton />
        ) : paginatedData.length === 0 ? (
          <div style={{ padding: "48px", textAlign: "center" }}>
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>📦</div>
            <p style={{ color: "#6c757d", fontFamily: "sans-serif", margin: 0 }}>No stock found</p>
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div
              style={{
                backgroundColor: "#f8f9fa",
                borderBottom: "1px solid #e9ecef",
                padding: "12px 16px"
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "3fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr",
                  gap: "16px",
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#495057",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  fontFamily: "sans-serif"
                }}
              >
                <div>Product</div>
                <div style={{ textAlign: "center" }}>Stock</div>
                <div style={{ textAlign: "center" }}>Store In</div>
                <div style={{ textAlign: "center" }}>Sales Return</div>
                <div style={{ textAlign: "center" }}>Adjustment</div>
                <div style={{ textAlign: "center" }}>Stock Out</div>
                
              
                <div style={{ textAlign: "center" }}>Unit</div>
                <div style={{ textAlign: "center" }}>PP</div>
                <div style={{ textAlign: "center" }}>Total Price</div>
              </div>
            </div>

            {/* Stock List */}
            <div style={{ borderTop: "1px solid #e9ecef" }}>
              {paginatedData.map((row, rowIndex) => {
                const imageUrl = getImageUrl(row);
                
                return (
                  <div
                    key={rowIndex}
                    style={{
                      padding: "16px",
                      borderBottom: "1px solid #e9ecef",
                      transition: "background-color 0.2s"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#f8f9fa";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "white";
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "3fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr",
                        gap: "16px",
                        alignItems: "center"
                      }}
                    >
                      {/* Product Column */}
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                          {/* Product Image */}
                          <div
                            style={{
                              position: "relative",
                              flexShrink: 0,
                              width: "96px",
                              height: "96px",
                              minWidth: "96px",
                              minHeight: "96px",
                              borderRadius: "8px",
                              overflow: "hidden",
                              border: "1px solid #e9ecef",
                              backgroundColor: "#f8f9fa",
                              aspectRatio: "1 / 1"
                            }}
                            className="product-image-group"
                          >
                            {imageUrl ? (
                              <>
                                <img
                                  src={imageUrl}
                                  alt={row.name || "Product"}
                                  loading="lazy"
                                  className="drug-thumbnail-img"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    objectPosition: "center",
                                    display: "block"
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    const placeholder = e.target.nextElementSibling;
                                    if (placeholder) {
                                      placeholder.style.display = 'flex';
                                    }
                                  }}
                                />
                                <button
                                  onClick={() => setZoomImage(imageUrl)}
                                  style={{
                                    position: "absolute",
                                    top: "4px",
                                    right: "4px",
                                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                                    color: "white",
                                    padding: "6px",
                                    borderRadius: "50%",
                                    border: "none",
                                    cursor: "pointer",
                                    opacity: 0,
                                    transition: "opacity 0.2s",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "24px",
                                    height: "24px",
                                    zIndex: 10
                                  }}
                                  className="zoom-button"
                                  title="Zoom image"
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.5)";
                                  }}
                                >
                                  <svg style={{ width: "16px", height: "16px" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                                  </svg>
                                </button>
                              </>
                            ) : null}
                            <div
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                backgroundColor: "#f8f9fa",
                                display: imageUrl ? "none" : "flex",
                                alignItems: "center",
                                justifyContent: "center"
                              }}
                            >
                              <span style={{ fontSize: "32px" }}>📦</span>
                            </div>
                            <style>{`
                              .product-image-group:hover .zoom-button {
                                opacity: 1 !important;
                              }
                            `}</style>
                          </div>

                          {/* Product Name */}
                          <div style={{ flex: 1, minWidth: 0, maxWidth: "100%" }}>
                            <div
                              style={{
                                fontSize: "16px",
                                fontWeight: "600",
                                color: "#343a40",
                                fontFamily: "sans-serif",
                                marginBottom: "4px",
                                lineHeight: "1.3em",
                                maxHeight:"3.9em",
                                overflow:"hidden"
                                
                              }}
                            >
                              {row.name || "N/A"}
                            </div>
                            {row.manufacturer && (
                              <div
                                style={{
                                  fontSize: "14px",
                                  color: "#6c757d",
                                  fontFamily: "sans-serif"
                                }}
                              >
                                {row.manufacturer}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Stock Column */}
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: "14px", color: "#495057", fontFamily: "sans-serif" }}>
                          {row?.stock ?? 0}
                        </div>
                      </div>

                      {/* Store In Column */}
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: "14px", color: "#495057", fontFamily: "sans-serif" }}>
                          {row?.store_in_sum_total_qty || 0}
                        </div>
                      </div>

                      {/* Sales Return Column */}
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: "14px", color: "#495057", fontFamily: "sans-serif" }}>
                          {row?.sales_return_sum_pcs || 0}
                        </div>
                      </div>

                      {/* Adjustment Column */}
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: "14px", color: "#495057", fontFamily: "sans-serif" }}>
                          {parseInt(row?.adjustment_sum_increase || 0) -
                            parseInt(row?.adjustment_sum_decrease || 0)}
                        </div>
                      </div>

                      {/* Stock Out Column */}
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: "14px", color: "#495057", fontFamily: "sans-serif" }}>
                          {row?.stock_out_sum_pcs || 0}
                        </div>
                      </div>

                      {/* Unit Column */}
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: "14px", color: "#495057", fontFamily: "sans-serif" }}>
                          {row?.unit || "N/A"}
                        </div>
                      </div>

                      {/* PP Column */}
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: "14px", color: "#495057", fontFamily: "sans-serif" }}>
                          {parseFloat(row.drug?.drug_price || 0).toFixed(2)}
                        </div>
                      </div>

                      {/* Total Price Column */}
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: "14px", fontWeight: "600", color: "#343a40", fontFamily: "sans-serif" }}>
                          {(() => {
                            const unitPrice = parseFloat(row.drug?.drug_price || 0) + parseFloat(row.drug?.vat ?? 0);
                            const qty = parseInt(row.stock || 0, 10);
                            const bonusQty = parseInt(row.bonus_qty || 0, 10);
                            const total = unitPrice * (qty - bonusQty);
                            return Number.isFinite(total) ? total.toFixed(2) : "0.00";
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div
                style={{
                  backgroundColor: "#f8f9fa",
                  padding: "16px 24px",
                  borderTop: "1px solid #e9ecef",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between"
                }}
              >
                <div style={{ fontSize: "14px", color: "#495057", fontFamily: "sans-serif" }}>
                  Showing {paginatedData.length} of {filteredData.length} items
                </div>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <button
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1 || spinner}
                    style={{
                      padding: "8px 16px",
                      border: "1px solid #e9ecef",
                      borderRadius: "8px",
                      backgroundColor: currentPage === 1 || spinner ? "#f5f5f5" : "white",
                      cursor: currentPage === 1 || spinner ? "not-allowed" : "pointer",
                      color: currentPage === 1 || spinner ? "#999" : "#495057",
                      fontFamily: "sans-serif",
                      transition: "background-color 0.2s"
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== 1 && !spinner) {
                        e.currentTarget.style.backgroundColor = "#e9ecef";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== 1 && !spinner) {
                        e.currentTarget.style.backgroundColor = "white";
                      }
                    }}
                  >
                    Previous
                  </button>
                  <span style={{ padding: "8px 16px", fontSize: "14px", color: "#495057", fontFamily: "sans-serif" }}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages || spinner}
                    style={{
                      padding: "8px 16px",
                      border: "1px solid #e9ecef",
                      borderRadius: "8px",
                      backgroundColor: currentPage === totalPages || spinner ? "#f5f5f5" : "white",
                      cursor: currentPage === totalPages || spinner ? "not-allowed" : "pointer",
                      color: currentPage === totalPages || spinner ? "#999" : "#495057",
                      fontFamily: "sans-serif",
                      transition: "background-color 0.2s"
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== totalPages && !spinner) {
                        e.currentTarget.style.backgroundColor = "#e9ecef";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== totalPages && !spinner) {
                        e.currentTarget.style.backgroundColor = "white";
                      }
                    }}
                  >
                    Next
                  </button>
                  <select
                    value={currentPage}
                    onChange={(e) => handlePageChange(Number(e.target.value))}
                    disabled={spinner}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #e9ecef",
                      borderRadius: "8px",
                      fontSize: "14px",
                      color: "#495057",
                      backgroundColor: "white",
                      fontFamily: "sans-serif",
                      cursor: spinner ? "not-allowed" : "pointer",
                      outline: "none"
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#a855f7";
                      e.target.style.boxShadow = "0 0 0 2px rgba(168, 85, 247, 0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e9ecef";
                      e.target.style.boxShadow = "none";
                    }}
                  >
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <option key={page} value={page}>
                        {page}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Summary Totals */}
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid #e9ecef",
          padding: "16px",
          marginTop: "24px"
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: "24px", alignItems: "center" }}>
          <div>
            <span style={{ fontSize: "14px", color: "#6c757d", fontFamily: "sans-serif", marginRight: "8px" }}>
              {lan?.totalStock || "Total Stock"}:
            </span>
            <span style={{ fontSize: "16px", fontWeight: "600", color: "#343a40", fontFamily: "sans-serif" }}>
              {total}
            </span>
          </div>
          <div>
            <span style={{ fontSize: "14px", color: "#6c757d", fontFamily: "sans-serif", marginRight: "8px" }}>
              {lan?.totalPurchasePrice || "Total Purchase Price"}:
            </span>
            <span style={{ fontSize: "16px", fontWeight: "600", color: "#343a40", fontFamily: "sans-serif" }}>
              ৳{totalPrice}
            </span>
          </div>
          <div>
            <span style={{ fontSize: "14px", color: "#6c757d", fontFamily: "sans-serif", marginRight: "8px" }}>
              {lan?.totalSellingPrice || "Total Selling Price"}:
            </span>
            <span style={{ fontSize: "16px", fontWeight: "600", color: "#343a40", fontFamily: "sans-serif" }}>
              ৳{totalSellingPrice}
            </span>
          </div>
        </div>
      </div>

      {/* Zoom Image Modal */}
      {zoomImage && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            backdropFilter: "blur(4px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px"
          }}
          onClick={() => setZoomImage(null)}
        >
          <div
            style={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "40px 16px 16px 16px"
            }}
          >
            <button
              onClick={() => setZoomImage(null)}
              style={{
                position: "absolute",
                top: "16px",
                right: "16px",
                backgroundColor: "white",
                color: "#333",
                border: "none",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "background-color 0.2s",
                zIndex: 10
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#e9ecef";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "white";
              }}
              title="Close"
            >
              <svg style={{ width: "24px", height: "24px" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={zoomImage}
              alt="Zoomed product"
              className="drug-zoom-img"
              style={{
                maxWidth: "90vw",
                maxHeight: "90vh",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                objectPosition: "center",
                borderRadius: "8px",
                display: "block"
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* CSV Import Dialog */}
      <Dialog disableEscapeKeyDown open={open} onClose={handleClose}>
        <DialogTitle sx={{ fontSize: "15px" }}>Import Data</DialogTitle>
        <DialogContent sx={{ padding: 0 }}>
          <FormControl sx={{ m: 1, minWidth: 150, padding: 0 }}>
            <input
              onChange={(e) => {
                setcsvFile(e.target.files[0]);
              }}
              type="file"
              className="ms-2"
              accept=".csv"
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={() => {
              console.log("csv", csvFile);
              if (csvFile !== null) {
                var formdata = new FormData();
                formdata.append("file", csvFile);

                http
                  .post("/stock-in-by-csv", formdata)
                  .then((res) => {
                    console.log("res", res.data);
                    toast.success(res.data.message);
                    setstateupdate(Math.random());
                    handleClose();
                  })
                  .catch((err) => {
                    console.log("err", err.response.data);
                    if (err.response.data.error !== undefined) {
                      toast.error(err.response.data.error);
                    } else if (err.response.data.name !== undefined) {
                      toast.error(err.response.data.name[0]);
                    } else if (err.response.data.price !== undefined) {
                      toast.error(err.response.data.price[0]);
                    } else if (err.response.data.stock !== undefined) {
                      toast.error(err.response.data.stock[0]);
                    } else if (err.response.data.manufacturer !== undefined) {
                      toast.error(err.response.data.manufacturer[0]);
                    } else if (err.response.data.drug_id !== undefined) {
                      toast.error(err.response.data.drug_id[0]);
                    } else if (err.response.data.pkt_size !== undefined) {
                      toast.error(err.response.data.pkt_size[0]);
                    } else if (err.response.data.box_type !== undefined) {
                      toast.error(err.response.data.box_type[0]);
                    } else if (err.response.data.unit !== undefined) {
                      toast.error(err.response.data.unit[0]);
                    } else if (err.response.data.rack !== undefined) {
                      toast.error(err.response.data.rack[0]);
                    } else if (err.response.data.shelf !== undefined) {
                      toast.error(err.response.data.shelf[0]);
                    } else if (err.response.data.expire_date !== undefined) {
                      toast.error(err.response.data.expire_date[0]);
                    } else {
                      toast.error("Opps Something is wrong !");
                    }
                    handleClose();
                  });
              } else {
                toast.warning("please select file");
              }
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CurrentStock;
